// The signed-in account menu: name, Billing (#30, accounts with billing to manage), Delete account
// (#34), Sign out. A free plan gets no Billing item; its plan is shown in the header instead (#126). Used by Layout and by the first-run setup screen (#123 S6b): a paying owner who hasn't created
// a squad yet must still reach Billing, so the menu can't live only inside Layout.
import { Link } from "wouter";
import { useClerk, useUser } from "@clerk/react";
import { useState, useRef, useEffect, useId } from "react";
import {
  getBillingPortalSession,
  getGetMySubscriptionQueryKey,
  useGetCurrentUser,
  useGetMySubscription,
  type BillingPortalUnavailable,
} from "@workspace/api-client-react";
import { freePlanLine } from "@/lib/freePlanLine";

export function UserMenu() {
  const { signOut } = useClerk();
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // #30 — disclosure pattern (Kayla): the trigger says whether the panel is open and which element
  // it controls. Not aria-haspopup="menu", which would promise arrow-key menu items the panel lacks.
  const panelId = useId();

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // WI#23 — sign-up doesn't collect a name, so fall back to the name the runner gave their squad
  // profile before the generic "Runner".
  const { data: currentUser } = useGetCurrentUser();
  // #126 — only an account with a subscription it can't manage (the free plan) needs the plan line,
  // so only then is /me/subscription read.
  const isFree = !!currentUser?.hasSubscription && currentUser?.canManageBilling === false;
  const { data: mySubscription } = useGetMySubscription({ query: { queryKey: getGetMySubscriptionQueryKey(), enabled: isFree } });
  const freePlanName = isFree ? mySubscription?.subscription?.planName : null;
  const freeLine = freePlanName ? freePlanLine(freePlanName) : null;
  const rosterName = currentUser?.members?.[0]?.name;
  const displayName = user?.firstName
    ? `${user.firstName}${user.lastName ? " " + user.lastName : ""}`
    : user?.username ?? rosterName ?? "Runner";
  const initials = displayName === "Runner"
    ? "?"
    : displayName.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  // WI#30 — Billing (change plan, cancel) lives in the PeakCommerce customer portal. Offered
  // only to someone the server says has a subscription, and when it can't be opened the
  // server's own message is shown, never a raw error page.
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingIssue, setBillingIssue] = useState<{ message: string; manageUrl?: string } | null>(null);
  async function openBilling() {
    setBillingBusy(true);
    setBillingIssue(null);
    try {
      const session = await getBillingPortalSession();
      window.location.assign(session.url);
    } catch (err) {
      const body = (err as { data?: BillingPortalUnavailable | null }).data;
      setBillingIssue(
        body?.error
          ? { message: body.error, manageUrl: body.manageUrl }
          : { message: "Couldn't open billing. Check your connection and try again." },
      );
      setBillingBusy(false);
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="Account menu"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-100 transition-colors"
      >
        <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold uppercase select-none">
          {initials}
        </div>
        <span className="text-sm font-medium text-foreground hidden sm:block">
          {displayName}
        </span>
        <svg className="w-3.5 h-3.5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div id={panelId} className="absolute right-0 top-full mt-1.5 w-56 bg-background border border-border rounded shadow-md z-50 py-1">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-xs font-semibold text-foreground truncate">{displayName}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.primaryEmailAddress?.emailAddress}</p>
            {/* #126 (Jordyn, Emily) — a free plan is account information, like the email: one muted line,
                never a disabled row among the actions. Name authored by the server. */}
            {freeLine && (
              <p className="text-xs text-muted-foreground truncate" data-testid="menu-plan-line">{freeLine}</p>
            )}
          </div>
          {currentUser?.canManageBilling && (
            <>
              <button
                onClick={openBilling}
                disabled={billingBusy}
                className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-zinc-50 transition-colors disabled:opacity-60"
              >
                {billingBusy ? "Opening billing..." : "Billing"}
              </button>
              {billingIssue && (
                <p role="status" className="px-3 pb-2 text-xs text-muted-foreground">
                  {billingIssue.message}
                  {billingIssue.manageUrl && (
                    <>
                      {" "}
                      <a href={billingIssue.manageUrl} className="underline" target="_blank" rel="noreferrer">
                        Open Apple subscriptions
                      </a>
                    </>
                  )}
                </p>
              )}
            </>
          )}
          {/* WI#34 — account deletion, offered to every signed-in account (Apple 5.1.1(v) parity). */}
          <Link
            href="/account/delete"
            onClick={() => setOpen(false)}
            className="block w-full text-left px-3 py-2 text-sm text-foreground hover:bg-zinc-50 transition-colors"
          >
            Delete account
          </Link>
          <button
            onClick={() => signOut()}
            className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-zinc-50 transition-colors"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
